import { Flex, Typography } from "antd";
import { ReactNode, useMemo, useRef } from "react";
import { usePopupPortal } from "../../../../contexts/PopupContext";
import { useTrackingOp } from "../../../../contexts/TrackingOpContext";
import { datetime } from "../../../../util/library/datetime";
import { EXECUTION_TYPE, OP_RUNNING_MODE } from "../../../../util/varibles/constants";
import { firstLetter, stepNameV2 } from "../../../../util/varibles/global";
import { IActivityLog, IHarvest2 } from "../../../../util/varibles/interface";
import Icon, { ICON_NAME } from "../../../Icon";
import FinishStepButton from "../FinishStepButton";
import { IDischarge } from "./_Discharging";
import DeliveryType from "./DeliveryType";
import { startDischarging } from "./DischargingPopup";
import Tanks from "./Tanks";

interface IProps {
    log: IActivityLog
    prev: IActivityLog
    discharging?: IActivityLog
    dischargingStepIndex: number
    stepIndex: number
}

export default function ArrivedFactory({ log, prev, discharging, dischargingStepIndex, stepIndex }: IProps) {
    const { openPopup, popupHolder } = usePopupPortal();
    const [op] = useTrackingOp(state => state.op?.operation as IHarvest2);
    const { delivery_types = [], execution_type, op_running_mode = OP_RUNNING_MODE.NORMAL, tanks = [] } = op || {};
    const infoRef = useRef({ execution_type, op_running_mode, stepIndex })
    const name = stepNameV2(log.key);
    const { real_start_time } = log;
    const { real_start_time: prev_time } = prev;
    const { sub_steps = [], real_sub_steps = [] } = discharging || {};
    const hasStartedDischarging = real_sub_steps.some(item => !!item.real_start_time);
    const disableDischarging = execution_type === EXECUTION_TYPE.AUTO || hasStartedDischarging || !real_start_time;

    const dischargingByTank = useMemo(() => {
        if (infoRef.current.execution_type === EXECUTION_TYPE.AUTO || infoRef.current.op_running_mode !== OP_RUNNING_MODE.DETAIL)
            return {};

        const deliveryTypesByTank = sub_steps.reduce((result: Record<string, IDischarge[]>, item) => {
            const { delivery_type_id = "", tank_id } = item;
            const deliveryType = delivery_types.find(delivery => delivery.id === delivery_type_id);
            result[tank_id] = [
                ...(result[tank_id] || []),
                {
                    ...item,
                    name: firstLetter((deliveryType?.name || delivery_type_id).replace(/_/g, " ")),
                    fish_amount: item.fish_amount || 0,
                    total_weight: item.total_weight || 0,
                }
            ];
            return result;
        }, {});

        return tanks.reduce((result: Record<string, ReactNode>, tank) => {
            const deliveryTypes = deliveryTypesByTank[tank.id] || [];
            result[tank.id] = <div className="flex flex-col gap-3 mt-[20px]">
                {deliveryTypes.map((deliveryType, index) => <DeliveryType
                    key={`${deliveryType.delivery_type_id}_${index}`}
                    data={deliveryType}
                    disabled={disableDischarging}
                    onStart={() => startDischarging({
                        deliveryTypes: delivery_types,
                        deliveryTypeId: deliveryType.delivery_type_id,
                        tankId: tank.id,
                        status: 0,
                        current_process: dischargingStepIndex,
                    }, openPopup)}
                />)}
                {!disableDischarging && <div className="flex justify-center py-[20px]">
                    <div
                        className="flex flex-col items-center cursor-pointer"
                        onClick={() => startDischarging({
                            deliveryTypes: delivery_types,
                            tankId: tank.id,
                            status: 0,
                            current_process: dischargingStepIndex,
                        }, openPopup)}
                    >
                        <Icon icon={ICON_NAME.ADD_3} bg="colorLink" size={18} />
                        <div className="link text-[14px] mt-[2px]">Select other delivery methods</div>
                    </div>
                </div>}
            </div>;
            return result;
        }, {});
    }, [delivery_types, disableDischarging, dischargingStepIndex, openPopup, sub_steps, tanks]);

    return <>
        <div className="w-full">
            <Flex className="h-[45px] items-center" gap={6}>
                <Typography.Text type="secondary">Arrived at factory: </Typography.Text>
                <Typography.Text>{real_start_time ? datetime(real_start_time).format("HH:mm, DD/MM") : "--"}</Typography.Text>
            </Flex>
            <div className="mt-3">
                <Tanks infoByTank={dischargingByTank} />
            </div>
        </div>
        {(execution_type !== EXECUTION_TYPE.AUTO && !!prev_time && !real_start_time) && <FinishStepButton
            name={name}
            payload={{ step_new: -1, current_process: stepIndex }}
            message="This will start the step. Time is logged via GPS, or from your action if GPS is unavailable."
        />}
        {popupHolder}
    </>
}
